import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormDialog, inputCls } from "@/components/prohub/shared";

export const KINDS = [
  { id: "subscription", label: "Subscriptions" },
  { id: "current_account", label: "Current accounts" },
  { id: "savings", label: "Savings" },
  { id: "credit_card", label: "Credit cards" },
];

const EMPTY = { kind: "subscription", name: "", provider: "", url: "https://", perk: "", active: true, service: "", keywords: "", monthly_price: "", rate: "", cashback_pct: "", monthly_cap: "" };
const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

export default function DealFormDialog({ open, onOpenChange, deal, defaultKind, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    setForm(deal
      ? { ...EMPTY, ...Object.fromEntries(Object.entries(deal).map(([k, v]) => [k, v ?? ""])), keywords: (deal.keywords || []).join(", ") }
      : { ...EMPTY, kind: defaultKind || "subscription" });
  }, [open, deal, defaultKind]);

  const k = form.kind;
  const within = (v, max) => num(v) > 0 && num(v) <= max;
  const valid = form.name.trim().length >= 2 && form.provider.trim() && /^https:\/\/\S+\.\S+/.test(form.url) && (
    k === "subscription" ? form.service.trim() && form.keywords.trim() && form.monthly_price !== "" && num(form.monthly_price) >= 0 && num(form.monthly_price) <= 1000
      : k === "savings" ? within(form.rate, 20)
        : k === "current_account" ? within(form.cashback_pct, 20) && within(form.monthly_cap, 1000)
          : within(form.cashback_pct, 20));

  const save = async () => {
    setSaving(true);
    const body = {
      kind: k, name: form.name.trim(), provider: form.provider.trim(), url: form.url.trim(), perk: form.perk.trim(), active: !!form.active,
      service: k === "subscription" ? form.service.trim() : null,
      keywords: k === "subscription" ? form.keywords.split(",").map((s) => s.trim()).filter(Boolean) : [],
      monthly_price: k === "subscription" ? num(form.monthly_price) : null,
      rate: k === "savings" ? num(form.rate) : null,
      cashback_pct: ["current_account", "credit_card"].includes(k) ? num(form.cashback_pct) : null,
      monthly_cap: k === "current_account" ? num(form.monthly_cap) : null,
    };
    try {
      if (deal) await api.put(`/admin/deals/${deal.id}`, body);
      else await api.post("/admin/deals", body);
      toast.success(deal ? "Deal updated" : "Deal added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={deal ? "Edit deal" : "Add a deal"} testid="deal-form-modal" onSave={save} saving={saving} disabled={!valid} saveLabel={deal ? "Save changes" : "Add deal"}>
      <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1 -mr-1">
        <div>
          <Label className="text-slate-300 text-sm">Type</Label>
          <Select value={k} onValueChange={(v) => setForm({ ...form, kind: v })} disabled={!!deal}>
            <SelectTrigger data-testid="deal-form-kind-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#14181D] border-white/10 text-white">
              {KINDS.map((x) => <SelectItem key={x.id} value={x.id} className="focus:bg-[#00C896]/20">{x.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {k === "subscription" && (
          <>
            <Field label="Service it replaces" testid="deal-form-service-input" value={form.service} onChange={set("service")} placeholder="Netflix" />
            <Field label="Match transactions containing (comma separated)" testid="deal-form-keywords-input" value={form.keywords} onChange={set("keywords")} placeholder="netflix" />
          </>
        )}
        <Field label="Alternative name" testid="deal-form-name-input" value={form.name} onChange={set("name")} placeholder="Netflix Standard with adverts" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Provider" testid="deal-form-provider-input" value={form.provider} onChange={set("provider")} placeholder="Netflix" />
          {k === "subscription" && <Field label="Price (£ / month)" testid="deal-form-price-input" type="number" min="0" step="0.01" value={form.monthly_price} onChange={set("monthly_price")} placeholder="5.99" />}
          {k === "savings" && <Field label="Rate (% AER)" testid="deal-form-rate-input" type="number" min="0" max="20" step="0.01" value={form.rate} onChange={set("rate")} placeholder="4.0" />}
          {["current_account", "credit_card"].includes(k) && <Field label="Cashback (%)" testid="deal-form-cashback-input" type="number" min="0" max="20" step="0.01" value={form.cashback_pct} onChange={set("cashback_pct")} placeholder="1" />}
        </div>
        {k === "current_account" && <Field label="Cashback cap (£ / month)" testid="deal-form-cap-input" type="number" min="0" step="0.01" value={form.monthly_cap} onChange={set("monthly_cap")} placeholder="15" />}
        <Field label="Learn more link" testid="deal-form-url-input" value={form.url} onChange={set("url")} placeholder="https://" />
        <div>
          <Label className="text-slate-300 text-sm">Short description</Label>
          <Textarea data-testid="deal-form-perk-input" className={`${inputCls} min-h-[70px]`} maxLength={160} value={form.perk} onChange={set("perk")} placeholder="What makes this a better deal" />
        </div>
      </div>
    </FormDialog>
  );
}
